// ============================================
// Content Config
// ============================================

import type { I18nConfig } from './i18n';
import type { LayoutConfig } from './layout';

// ============================================
// Display Config
// ============================================

/**
 * Editor display options - control visibility of title/description fields in editor
 */
export interface DisplayConfig {
    /** Show title field in content editor (default: true) */
    showTitle?: boolean;
    /** Show description field in content editor (default: true) */
    showDescription?: boolean;
}

/**
 * Completion rules for the Documents (Tài liệu) content type
 */
export interface DocumentsCompletionConfig {
    /** Learner must scroll through the full page viewport before marking done */
    requireFullScroll?: boolean;
    /** Minimum seconds the player must be visible (0 = disabled). */
    minimumViewSeconds?: number;
}

/**
 * Complete configuration for content items
 */
export interface ContentConfig {
    theme?: ThemeConfig;
    behavior?: BehaviorConfig;
    i18n?: I18nConfig;
    scoring?: ScoringConfig;
    timer?: TimerConfig;
    /** Editor display options (title, description visibility) */
    display?: DisplayConfig;
    /** Layout config – extended by content types (splitRatio, displayMode, playerView, inlineSinglePageNav, etc.) */
    layout?: Partial<LayoutConfig> & {
        splitRatio?: string;
        minHeight?: string;
        playerView?: string;
        /** MCQ-style sets: `all` | `tabs` multi-page (legacy stored `step` coerced to `tabs` in players). */
        displayMode?: 'all' | 'step' | 'tabs';
    };
    /** Documents content type: scroll + dwell time gates for “mark done” */
    documents?: DocumentsCompletionConfig;
    /**
     * Zero-based offset for question numbering in composite players (question-set, qt-dropdown, mcq-set).
     * When set, displayed question numbers start from `questionOffset + 1` instead of `1`.
     * Embed URL param: `?questionOffset=5` -> questions numbered 6, 7, 8, ...
     */
    questionOffset?: number;
}

// ============================================
// Theme Config
// ============================================

/**
 * Visual theme configuration
 */
export interface ThemeConfig {
    primaryColor?: string;
    secondaryColor?: string;
    successColor?: string;
    errorColor?: string;
    warningColor?: string;
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: string;
    fontFamily?: string;
    fontSize?: string;
}

// ============================================
// Behavior Config
// ============================================

/**
 * Behavioral settings for content interaction
 */
export interface BehaviorConfig {
    /** Allow learner to retry after incorrect answer */
    allowRetry?: boolean;
    /** Maximum number of attempts (0 = unlimited) */
    maxAttempts?: number;
    /** Shuffle choice options */
    shuffleChoices?: boolean;
    /** Show correct answer after submission */
    showCorrectAnswer?: boolean;
    /** Show explanation after submission */
    showExplanation?: boolean;
    /** Show hint button */
    showHint?: boolean;
    /** Show feedback immediately after selection */
    instantFeedback?: boolean;
    /** Exam mode - no feedback until completion */
    examMode?: boolean;
    /** Auto advance to next content after completion */
    autoAdvance?: boolean;
}

// ============================================
// Scoring Config
// ============================================

/**
 * Scoring configuration for gradeable content
 */
export interface ScoringConfig {
    /** Maximum possible score */
    max?: number;
    /** Enable partial credit scoring */
    partialCreditEnabled?: boolean;
    /** Penalty for each wrong answer */
    penaltyPerWrongAnswer?: number;
    /** Score required to pass (0-1) */
    passingScore?: number;
}

// ============================================
// Timer Config
// ============================================

/**
 * Timer settings for timed content
 */
export interface TimerConfig {
    /** Enable countdown timer */
    enabled?: boolean;
    /** Duration in seconds */
    duration?: number;
    /** Warning threshold in seconds */
    warningThreshold?: number;
    /** Auto-submit when timer expires */
    autoSubmitOnExpire?: boolean;
}

/**
 * ELMS-2370: force `behavior.showCorrectAnswer=false` (LMS "hide answer" assignments).
 * Pure; returns the same reference when `hide` is false so default behaviour is untouched.
 */
export function applyHideCorrectAnswersToConfig<T extends ContentConfig | undefined>(
    config: T,
    hide: boolean | undefined,
): T | ContentConfig {
    if (!hide) {
        return config;
    }
    return {
        ...(config ?? {}),
        behavior: { ...(config?.behavior ?? {}), showCorrectAnswer: false },
    };
}

/** ELMS-2370: false when `behavior.showCorrectAnswer === false` (default: reveal). */
export function isCorrectAnswerRevealed(
    config: { behavior?: { showCorrectAnswer?: boolean } } | undefined | null,
): boolean {
    return config?.behavior?.showCorrectAnswer !== false;
}

/** `details` keys written by graders that carry the answer / rationale (mcq, fitb, …). */
const ANSWER_BEARING_DETAIL_KEYS = ['explanation', 'correctChoice'] as const;

/**
 * ELMS-2370: strip `correctAnswer` and answer-bearing `details` from a result that is
 * posted out of the player (PLAYER_SUBMIT). Score/max/percentage/answer/attempts and
 * per-item booleans (itemResults/perBlank/correctCount) are kept. Pure; no mutation.
 */
export function redactCorrectAnswersFromResult<T extends { correctAnswer?: unknown; details?: Record<string, unknown> }>(
    result: T,
): T {
    const { correctAnswer: _omit, details, ...rest } = result;
    void _omit;
    const next: Record<string, unknown> = { ...rest };
    if (details) {
        const kept: Record<string, unknown> = { ...details };
        for (const k of ANSWER_BEARING_DETAIL_KEYS) {
            delete kept[k];
        }
        next.details = kept;
    }
    return next as T;
}
